/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { MdArticle, MdNavigateNext, MdPictureAsPdf } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import html2pdf from 'html2pdf.js';
import PropTypes from 'prop-types';

import {
    Card,
    CardContent,
    Typography,
    Chip,
    Breadcrumbs,
    Button,
} from '@material-ui/core';

import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';

import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';

export default function ArticleView(props) {
    const dispatch = useDispatch();
    const { match } = props;
    const article_id = match.params.id;

    const [isLoading, setIsLoading] = useState(true);
    const [article, setArticle] = useState(null);
    const [forbidden, setForbidden] = useState(false);

    function requestError(error) {
        if (error.response) {
            const message = (
                <LocaleMessage msg={`errors.${error.response.data.code}`} />
            );
            const { status } = error.response;
            if (status === 401) {
                dispatch(expireSession());
            } else if (status === 403 || status === 404) {
                setForbidden(true);
            }
            toast.error(message);
        } else if (error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
        setIsLoading(false);
    }

    async function loadArticle(_id) {
        setIsLoading(true);
        await api
            .get(`articles/public/${_id}`)
            .then(response => {
                setArticle(response.data);
            })
            .catch(error => requestError(error));
        setIsLoading(false);
    }

    useEffect(() => {
        loadArticle(article_id);
    }, [article_id]);

    function handleExportPdf() {
        const categoryText = article.category
            ? `${
                  article.category.parent
                      ? `${article.category.parent.name} > `
                      : ''
              }${article.category.name}`
            : '';

        const authorText = article.created ? article.created.name : '';

        const container = document.createElement('div');
        container.innerHTML = `
            <div style="font-family: Arial, sans-serif; padding: 20px;">
                <h1 style="margin-bottom: 10px;">${article.title}</h1>
                ${
                    categoryText
                        ? `<div style="color: #666; font-size: 14px; margin-bottom: 20px;">${categoryText}</div>`
                        : ''
                }
                <div style="line-height: 1.6;">${article.content}</div>
            </div>
        `;

        const opt = {
            margin: [10, 10, 20, 10],
            filename: `${article.title}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        };

        html2pdf()
            .set(opt)
            .from(container)
            .toPdf()
            .get('pdf')
            .then(pdf => {
                const totalPages = pdf.internal.getNumberOfPages();
                const pageHeight = pdf.internal.pageSize.getHeight();

                for (let i = 1; i <= totalPages; i += 1) {
                    pdf.setPage(i);
                    pdf.setFontSize(9);
                    pdf.setTextColor(100);
                    pdf.text(authorText, 10, pageHeight - 10);
                }
            })
            .save();
    }

    function renderCategoryBreadcrumbs() {
        if (!article || !article.category) return null;

        const breadcrumbItems = [];

        if (article.category.parent) {
            breadcrumbItems.push(
                <Chip
                    key="parent"
                    size="small"
                    label={article.category.parent.name}
                    style={{ cursor: 'default' }}
                />
            );
        }

        breadcrumbItems.push(
            <Chip
                key="category"
                size="small"
                label={article.category.name}
                color="primary"
                style={{ cursor: 'default' }}
            />
        );

        return (
            <Breadcrumbs
                separator={<MdNavigateNext fontSize="small" />}
                style={{ marginBottom: '20px' }}
            >
                {breadcrumbItems}
            </Breadcrumbs>
        );
    }

    return (
        <PageContent
            title={
                article ? (
                    article.title
                ) : (
                    <LocaleMessage msg="page.knowledge_base.article" />
                )
            }
            breadcrumbs={[
                {
                    url: '/',
                    title: <LocaleMessage msg="breadcrumbs.home" />,
                },
                {
                    url: '/knowledge-base',
                    title: <LocaleMessage msg="breadcrumbs.knowledge_base" />,
                },
            ]}
            loading={isLoading}
            forbidden={forbidden}
        >
            {article && (
                <div style={{ padding: '20px' }}>
                    <Card>
                        <CardContent>
                            {renderCategoryBreadcrumbs()}

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '20px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '15px',
                                    }}
                                >
                                    <MdArticle size={32} color="#1976d2" />
                                    <Typography variant="h4">
                                        {article.title}
                                    </Typography>
                                </div>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<MdPictureAsPdf />}
                                    onClick={handleExportPdf}
                                >
                                    PDF
                                </Button>
                            </div>

                            <div
                                className="article-content"
                                style={{
                                    padding: '20px',
                                    backgroundColor: '#fafafa',
                                    borderRadius: '8px',
                                    minHeight: '300px',
                                }}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: article.content,
                                }}
                            />

                            <div
                                style={{
                                    marginTop: '30px',
                                    paddingTop: '20px',
                                    borderTop: '1px solid #eee',
                                    color: '#666',
                                    fontSize: '0.9em',
                                }}
                            >
                                {article.created && (
                                    <Typography variant="body2">
                                        <LocaleMessage msg="page.knowledge_base.created_by" />
                                        {': '}
                                        {article.created.name}
                                    </Typography>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </PageContent>
    );
}

ArticleView.propTypes = {
    match: PropTypes.object.isRequired,
};
